import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_MIN_INTERVAL = 250;
const DEFAULT_MAX_INTERVAL = 10000;

interface UseDistributionTimerProps {
    isPlaying: boolean;
    currentIndex: number;
    totalLength: number;
    onIndexChange: (index: number) => void;
    onPlayingChange: (isPlaying: boolean) => void;
    disabled: boolean;
}

interface DistributionTimer {
    togglePlay: () => void;
    stopPlay: () => void;
    startPlay: () => void;
}

type TimerType = ReturnType<typeof setInterval>;

export const useDistributionTimer = ({
    isPlaying,
    currentIndex,
    totalLength,
    onIndexChange,
    onPlayingChange,
    disabled,
}: UseDistributionTimerProps): DistributionTimer => {
    const timerRef = useRef<TimerType | undefined>(undefined);

    const stopPlay = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = undefined;
        }
        onPlayingChange(false);
    }, [onPlayingChange]);

    const startPlay = useCallback(() => {
        if (currentIndex === totalLength - 1) {
            onIndexChange(0); // Reset to start
        }
        onPlayingChange(true);
    }, [currentIndex, totalLength, onIndexChange, onPlayingChange]);

    const togglePlay = useCallback(() => {
        if (isPlaying) {
            stopPlay();
        } else {
            startPlay();
        }
    }, [isPlaying, startPlay, stopPlay]);

    useEffect(() => {
        if (!isPlaying || disabled) return;

        // Calculate interval based on data length
        const interval =
            totalLength > 20 ? DEFAULT_MAX_INTERVAL / totalLength : DEFAULT_MIN_INTERVAL;

        timerRef.current = setInterval(() => {
            if (currentIndex < totalLength - 1) {
                onIndexChange(currentIndex + 1);
            } else {
                stopPlay();
            }
        }, interval);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isPlaying, currentIndex, totalLength, disabled, onIndexChange, stopPlay]);

    return {
        togglePlay,
        stopPlay,
        startPlay,
    };
};
